import React, { createContext, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";

interface JwtPayload {
  user_id: number;
  role: string;
}

interface AuthContextType {
  user: true | null;
  userId: number | null;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<true | null>(null);
  const [userId, setUserId] = useState<number | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem("jwt");
    if (token) {
      try {
        const decoded = jwtDecode<JwtPayload>(token);
        setUser(true);
        setUserId(decoded.user_id);
      } catch (err) {
        console.error("Invalid token", err);
        sessionStorage.removeItem("jwt");
      }
    }
  }, []);

  const login = (token: string) => {
    sessionStorage.setItem("jwt", token);
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      setUser(true);
      setUserId(decoded.user_id);
    } catch (err) {
      console.error("Failed to decode token", err);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("jwt");
    setUser(null);
    setUserId(null);
  };

  return (
    <AuthContext.Provider value={{ user, userId, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Returns the current user's id — throws if not logged in
export const useCurrentUser = (): number => {
  const { userId } = useAuth();
  if (userId === null) throw new Error("No authenticated user");
  return userId;
};
